import React, {
    PropsWithChildren,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react"
import { CSSTransition } from "react-transition-group"
import i18nData from "@rogue_4/locales/zh-CN.json"
import { StyledComponent } from "@rogue_4/types"
import SKland from "@skland/one-bridge"
import classNames from "classnames"
import dayjs from "dayjs"
import Scroll from "@hg/scroll"

import { CommonTransition } from "@/topic/rogue_4/components/CommonTransition"
import { GlobalContext } from "@/topic/rogue_4/context"
import { i18n } from "@/utils/i18n"
import { isMobile } from "@/utils/misc"
import { GAME_EQUIP_DICT } from "../../../data/battle"
import CHARACTER_DICT from "../../../data/character"
import GAME_RESULT_DICT from "../../../data/result"
import { useGameEquip, useGameStore } from "../../../store"
import {
    downloadImage,
    drawShareImage,
    getBase64Image,
} from "../../../utils/share"
import { SFX } from "../../../utils/sound"
import { getTransitionClassnames } from "../../../utils/transition"
import Button from "../../Button"
import EquipFrame, { CharacterImage } from "../../EquipFrame"
import Health from "../../Health"
import { useGameModalStore, useGameRuleModal } from "../store"

import styles from "./index.module.less"

export const GameCharacterStatic: StyledComponent<{ characterKey: string }> = ({
    className,
    style,
    characterKey,
}) => {
    return (
        <div
            className={classNames(
                styles.character,
                styles[characterKey],
                className
            )}
            style={style}
        >
            <div className={styles.filterContainer}>
                <div className={styles.img}></div>
            </div>
        </div>
    )
}

const TextBar: StyledComponent<
    PropsWithChildren<{ size?: "long" | "short"; character?: string }>
> = ({ className, style, children, size = "short", character }) => {
    return (
        <div
            className={classNames(
                styles.textBar,
                size === "long" && styles.long,
                className
            )}
            style={style}
        >
            <div className={styles.bg}>
                <div
                    className={styles.head}
                    style={{
                        backgroundColor: character
                            ? CHARACTER_DICT[character].themeColor
                            : undefined,
                    }}
                ></div>
                <div
                    className={styles.middle}
                    style={{
                        backgroundImage: character
                            ? `linear-gradient(to right, ${CHARACTER_DICT[character].themeColor} 0, #6c5531 20%)`
                            : undefined,
                    }}
                ></div>
                <div className={styles.tail}></div>
            </div>
            <div className={styles.content}>{children}</div>
        </div>
    )
}

const ShareModal: StyledComponent = ({ className, style }) => {
    const {
        state: { user },
    } = useContext(GlobalContext)

    const [active, setActive] = useState(false)
    useEffect(() => {
        useGameModalStore.setState({
            callShareModal: () => {
                setActive(true)
            },
        })
    }, [])

    const rolled = useGameStore((state) => state.rolled)
    const callRuleModal = useGameRuleModal()

    // switch character
    const [switchActive, setSwitchActive] = useState(false)
    const handleSwitch = useCallback(() => {
        setSwitchActive(true)
        SFX.play("commonClick")
    }, [])
    const handleClose = useCallback(() => {
        setActive(false)
        // setTimeout(() => {
        //     setSwitchActive(false)
        // }, 300)
    }, [])
    const handleConfirm = useCallback(() => {
        SFX.play("commonClick")
        setSwitchActive(false)
    }, [])
    const [activeCharacter, setActiveCharacter] = useState("nymph")
    const gameResult = useGameStore((state) => state.result)
    const result = useMemo(
        () => (gameResult ? GAME_RESULT_DICT[gameResult.key] : undefined),
        [gameResult]
    )
    const { equipArr } = useGameEquip()
    const [shareImageActive, setShareImageActive] = useState(false)
    const [imageData, setImageData] = useState("")
    const share = useCallback(
        async (download: boolean) => {
            if (download) {
                downloadImage("Sarkaz's Furnaceside Fables", imageData)
            } else {
                const { img, base64 } = await getBase64Image(imageData)
                SKland.shareImage({
                    html: img,
                    base64,
                })
            }
        },
        [imageData]
    )
    const callShareImage = useCallback(async () => {
        if (!user || !gameResult || !result) {
            return
        }

        const imageData = await drawShareImage(
            user.uid,
            user.nickName,
            activeCharacter,
            gameResult.health.nymph,
            gameResult.health.kalts,
            `${i18n(i18nData, "rogue_4.game.result.gainResult")}：${result ? result.nameCn : ""}`,
            equipArr.map((e) => e.key)
        )
        setImageData(imageData)
        setShareImageActive(true)
    }, [activeCharacter, equipArr, gameResult, result, user])
    const handleCloseShareImage = useCallback(() => {
        useGameStore.setState({ shared: true })

        const now = dayjs().unix()
        if (!rolled && now < __ROGUE_4_LOTTERY_END_AT) {
            callRuleModal()
        }
        setShareImageActive(false)
    }, [callRuleModal, rolled])
    return (
        <CSSTransition
            in={active}
            timeout={300}
            classNames={getTransitionClassnames(styles)}
        >
            <div
                className={classNames(styles.shareModal, className)}
                style={style}
                onClick={(e) => {
                    e.stopPropagation()
                }}
            >
                <CommonTransition state={switchActive ? "1" : "2"}>
                    {!switchActive ? (
                        <div className={styles.contentContainer}>
                            <TextBar>
                                {i18n(
                                    i18nData,
                                    "rogue_4.game.share.content.title"
                                )}
                            </TextBar>
                            <div className={styles.equipContainer}>
                                {equipArr.map((e, index) => (
                                    <EquipFrame
                                        className='shareEquip'
                                        key={index}
                                        equip={GAME_EQUIP_DICT[e.key]}
                                    />
                                ))}
                            </div>
                            <TextBar className={styles.label2}>
                                {i18n(
                                    i18nData,
                                    "rogue_4.game.share.content.result"
                                )}
                            </TextBar>
                            <div className={styles.health}>
                                <Health
                                    border
                                    health={
                                        gameResult ? gameResult.health.nymph : 0
                                    }
                                />
                                <div className={styles.vs}>VS</div>
                                <Health
                                    border
                                    health={
                                        gameResult ? gameResult.health.kalts : 0
                                    }
                                    kalts
                                />
                            </div>
                            <div className={styles.description}>
                                <div className={styles.line}>
                                    {i18n(
                                        i18nData,
                                        "rogue_4.game.result.gainResult"
                                    )}
                                    ：{result ? result.nameCn : ""}
                                </div>
                                <div className={styles.line}></div>
                            </div>
                            <TextBar className={styles.shareNotice} size="long">
                                {i18n(
                                    i18nData,
                                    "rogue_4.game.share.content.shareNotice"
                                )}
                            </TextBar>
                            <div
                                className={styles.buttonShare}
                                onClick={callShareImage}
                            />
                        </div>
                    ) : (
                        <div
                            className={classNames(
                                styles.contentContainer,
                                styles.switchContainer
                            )}
                        >
                            <Scroll
                                direction={"y"}
                                className={styles.scroll}
                                scrollBar={false}
                                rotateDetection
                            >
                                <div className={styles.innerContainer}>
                                    <div className={styles.line}>
                                        <CharacterImage
                                            className='innerType'
                                            key="nymph"
                                            label={CHARACTER_DICT["nymph"].name}
                                            onClick={() => {
                                                SFX.play("commonClick")
                                                setActiveCharacter("nymph")
                                            }}
                                        >
                                            <GameCharacterStatic characterKey="nymph" />
                                        </CharacterImage>
                                        <CharacterImage
                                            className='innerType'
                                            key="ermengarde"
                                            label={
                                                CHARACTER_DICT["ermengarde"]
                                                    .name
                                            }
                                            onClick={() => {
                                                SFX.play("commonClick")
                                                setActiveCharacter("ermengarde")
                                            }}
                                        >
                                            <GameCharacterStatic characterKey="ermengarde" />
                                        </CharacterImage>
                                        <CharacterImage
                                            className='innerType'
                                            key="mitm"
                                            label={CHARACTER_DICT["mitm"].name}
                                            onClick={() => {
                                                SFX.play("commonClick")
                                                setActiveCharacter("mitm")
                                            }}
                                        >
                                            <GameCharacterStatic characterKey="mitm" />
                                        </CharacterImage>
                                    </div>
                                    <div className={styles.line}>
                                        <CharacterImage
                                            className='innerType'
                                            key="crownie"
                                            label={
                                                CHARACTER_DICT["crownie"].name
                                            }
                                            onClick={() => {
                                                SFX.play("commonClick")
                                                setActiveCharacter("crownie")
                                            }}
                                        >
                                            <GameCharacterStatic characterKey="crownie" />
                                        </CharacterImage>
                                        <CharacterImage
                                            className='innerType'
                                            key="qalaisa"
                                            label={
                                                CHARACTER_DICT["qalaisa"].name
                                            }
                                            onClick={() => {
                                                SFX.play("commonClick")
                                                setActiveCharacter("qalaisa")
                                            }}
                                        >
                                            <GameCharacterStatic characterKey="qalaisa" />
                                        </CharacterImage>
                                        <CharacterImage
                                            className='innerType'
                                            key="tinman"
                                            label={
                                                CHARACTER_DICT["tinman"].name
                                            }
                                            onClick={() => {
                                                SFX.play("commonClick")
                                                setActiveCharacter("tinman")
                                            }}
                                        >
                                            <GameCharacterStatic characterKey="tinman" />
                                        </CharacterImage>
                                    </div>
                                    <div className={styles.line}>
                                        <CharacterImage
                                            className='innerType'
                                            key="hraaselsuher"
                                            label={
                                                CHARACTER_DICT["hraaselsuher"]
                                                    .name
                                            }
                                            onClick={() => {
                                                SFX.play("commonClick")
                                                setActiveCharacter(
                                                    "hraaselsuher"
                                                )
                                            }}
                                        >
                                            <GameCharacterStatic characterKey="hraaselsuher" />
                                        </CharacterImage>
                                        <CharacterImage
                                            className='innerType'
                                            key="kalts"
                                            label={CHARACTER_DICT["kalts"].name}
                                            onClick={() => {
                                                SFX.play("commonClick")
                                                setActiveCharacter("kalts")
                                            }}
                                        >
                                            <GameCharacterStatic characterKey="kalts" />
                                        </CharacterImage>
                                    </div>
                                </div>
                            </Scroll>
                            <div className={styles.desc}>
                                {i18n(
                                    i18nData,
                                    "rogue_4.game.share.content.switchCharacter"
                                )}
                            </div>
                            <TextBar
                                character={activeCharacter}
                                className={styles.shareDescription}
                            >
                                {
                                    CHARACTER_DICT[activeCharacter]
                                        .shareDescription
                                }
                            </TextBar>
                            <Button
                                size="small"
                                active={false}
                                className={styles.confirm}
                                onClick={handleConfirm}
                            >
                                {i18n(
                                    i18nData,
                                    "rogue_4.game.share.content.switchConfirm"
                                )}
                            </Button>
                        </div>
                    )}
                </CommonTransition>
                <div className={styles.characterContainer}>
                    <CommonTransition state={activeCharacter}>
                        <GameCharacterStatic characterKey={activeCharacter} />
                    </CommonTransition>
                    <div className={styles.title}>
                        <div className={styles.docName}>
                            Dr.{user?.nickName.split("#")?.[0]}
                        </div>
                        <div className={styles.battleReport}>
                            <div>BATTLE</div>
                            <div>REPORT</div>
                        </div>
                    </div>
                    <div
                        className={classNames(
                            styles.switch,
                            !switchActive && styles.active
                        )}
                        onClick={handleSwitch}
                    />
                </div>

                <div className={styles.closeButton} onClick={handleClose}></div>
                <div
                    className={classNames(
                        styles.shareImage,
                        shareImageActive && styles.active
                    )}
                    onClick={handleCloseShareImage}
                >
                    <img
                        className={styles.img}
                        src={imageData}
                        onTouchStart={() => share(false)}
                    />

                    {!isMobile() ? (
                        <Button
                            size="small"
                            active={false}
                            onClick={() => {
                                share(true)
                                SFX.play("commonClick")
                            }}
                            className={styles.button}
                        >
                            {i18n(i18nData, "rogue_4.game.result.image.save")}
                        </Button>
                    ) : null}
                </div>
            </div>
        </CSSTransition>
    )
}

export default ShareModal
